import { useEffect, useRef, useState } from "react";
import { useSettings } from "@/lib/useSettings";
import Icon from "@/components/Icon";
import { Reveal, useCountUp } from "@/lib/motionSafe";

function StatCard({ stat, active }) {
  const value = useCountUp(stat.value, { when: active });
  return (
    <div className="text-center px-4 py-8" data-testid={`stat-${stat.key}`}>
      <div className="font-heading text-5xl sm:text-6xl font-bold text-[#00E5FF] leading-none tabular-nums">
        {value.toLocaleString("en-US")}
        <span className="text-3xl align-top ms-1">{stat.suffix}</span>
      </div>
      <div className="mt-3 text-slate-300 text-sm md:text-base font-medium">{stat.label_ar}</div>
    </div>
  );
}

const REASONS = [
  { icon: "shield-check", title_ar: "موثوقية مطلقة", body_ar: "أجهزة معتمدة عالمياً وضمان شامل على كل مشروع." },
  { icon: "clock", title_ar: "التزام بالمواعيد", body_ar: "خطة زمنية واضحة وتسليم في الوقت المتفق عليه." },
  { icon: "wrench", title_ar: "دعم فني دائم", body_ar: "فريق دعم يعمل على مدار الساعة لخدمة عملائنا." },
  { icon: "sparkles", title_ar: "تقنيات حديثة", body_ar: "نتبنى أحدث التقنيات ونطبّقها بجودة تصنيع أوروبية." },
];

export default function WhyUs() {
  const { settings } = useSettings();
  const stats = settings?.stats || [];
  const [active, setActive] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(true)),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section ref={ref} className="relative py-20 md:py-28 bg-[#0A1628] text-white overflow-hidden" data-testid="why-us-section">
      <div className="aman-glow-orb -top-24 start-1/3 opacity-40" aria-hidden="true" />
      <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
        <Reveal>
          <div className="max-w-2xl">
            <span className="aman-tag">لماذا أمان؟</span>
            <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
              {settings?.whyus_section_title_ar || "أكثر من عقد من الخبرة في حماية المنشآت الفلسطينية"}
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              {settings?.whyus_section_desc_ar || "نجمع بين الخبرة الميدانية، جودة الأجهزة، والتكنولوجيا الحديثة لنقدم لعملائنا راحة بال حقيقية."}
            </p>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mt-14 border-y border-white/10 py-6" data-testid="stats-row">
          {stats.map((st) => (
            <StatCard key={st.key} stat={st} active={active} />
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-14">
          {REASONS.map((r, i) => (
            <Reveal key={r.icon} delay={i * 80}>
              <div className="aman-card-dark p-6 h-full">
                <div className="w-12 h-12 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/30 text-[#00E5FF] flex items-center justify-center">
                  <Icon name={r.icon} size={22} />
                </div>
                <h3 className="font-heading text-lg font-bold mt-4">{r.title_ar}</h3>
                <p className="text-slate-400 text-sm mt-2 leading-relaxed">{r.body_ar}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
